import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import BackLink from "../components/BackLink";
import { useApi } from "../apiHelpers";
import PageLayout from "../components/ui/PageLayout";
import { buttonClass } from "../components/ui/Button";
import { Field, TextArea } from "../components/ui/Field";
import { Tag } from "../components/ui/Tag";
import { CATEGORY } from "../components/ui/categoryColors";
import { Alert } from "../components/ui/Alert";
import Reveal from "../components/ui/Reveal";
import { Dialog } from "../components/ui/Dialog";
import { EmptyState } from "../components/ui/EmptyState";
import { LogoSpinner } from "../components/ui/LogoSpinner";
import { Panel } from "../components/ui/Panel";
import { StepStrip } from "../components/ui/StepStrip";
import { useToast } from "../components/Toast/ToastContext";

// Status colours as Tag data: each status keeps its own hue.
const statusHex = {
    pending_review: CATEGORY.yellow.ink.hex,
    approved: CATEGORY.blue.ink.hex,
    partially_approved: CATEGORY.orange.ink.hex,
    awaiting_receipt: CATEGORY.purple.ink.hex,
    received: CATEGORY.indigo.ink.hex,
    in_repair: CATEGORY.orange.ink.hex,
    quality_check: CATEGORY.cyan.ink.hex,
    ready_to_ship: CATEGORY.teal.ink.hex,
    shipped: CATEGORY.emerald.ink.hex,
    completed: CATEGORY.green.ink.hex,
    rejected: CATEGORY.red.ink.hex,
    on_hold: CATEGORY.amber.ink.hex,
    cancelled: CATEGORY.neutral.ink.hex
};

const itemStatusHex = {
    pending_review: CATEGORY.yellow.ink.hex,
    approved: CATEGORY.green.ink.hex,
    rejected: CATEGORY.red.ink.hex
};

const repairStatusHex = {
    pending: CATEGORY.neutral.ink.hex,
    received: CATEGORY.indigo.ink.hex,
    in_repair: CATEGORY.blue.ink.hex,
    repaired: CATEGORY.cyan.ink.hex,
    quality_check: CATEGORY.yellow.ink.hex,
    completed: CATEGORY.green.ink.hex,
    unrepairable: CATEGORY.red.ink.hex
};

const statusSteps = [
    { key: "pending_review", label: "Pending Review" },
    { key: "approved", label: "Approved" },
    { key: "awaiting_receipt", label: "Awaiting Receipt" },
    { key: "received", label: "Received" },
    { key: "in_repair", label: "In Repair" },
    { key: "quality_check", label: "Quality Check" },
    { key: "ready_to_ship", label: "Ready to Ship" },
    { key: "shipped", label: "Shipped" },
    { key: "completed", label: "Completed" }
];

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const formatDateShort = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    });
};

const formatStatus = (status) => {
    if (!status) return "-";
    return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
};

// The drone page embeds this in a padded slide-in panel: stack, no page gutter or rail.
const EmbeddedLayout = ({ header, rail, aside, children }) => (
    <div className="space-y-4">{header}{children}{rail}{aside}</div>
);

const RMADetails = ({ rmaNumberOverride, highlightSerial }) => {
    const { id: urlId } = useParams();
    const id = rmaNumberOverride || urlId;
    const api = useApi();
    const toast = useToast();

    const [rma, setRma] = useState(null);
    const [messages, setMessages] = useState([]);
    const [statusHistory, setStatusHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Forms
    const [newMessage, setNewMessage] = useState("");
    const [newInternalNote, setNewInternalNote] = useState("");
    const [showRejectItemModal, setShowRejectItemModal] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [showHoldModal, setShowHoldModal] = useState(false);
    const [showRejectedItems, setShowRejectedItems] = useState(false);
    const [holdReason, setHoldReason] = useState("");
    const [estimatedDate, setEstimatedDate] = useState("");

    // Refs for auto-scroll within messages container
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const [shouldScrollMessages, setShouldScrollMessages] = useState(false);

    useEffect(() => {
        fetchRMA();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Auto-scroll messages to bottom only when a new message is sent (not on load)
    useEffect(() => {
        if (shouldScrollMessages && messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            setShouldScrollMessages(false);
        }
    }, [messages, shouldScrollMessages]);

    const fetchRMA = async () => {
        try {
            const data = await api.get(`/rma/${id}`);
            setRma(data);
            setMessages(data.messages || []);
            setStatusHistory(data.status_history || []);
            if (data.estimated_completion_date) {
                setEstimatedDate(data.estimated_completion_date.split('T')[0]);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const performAction = async (endpoint, body = {}, successMessage) => {
        setActionLoading(true);
        setError(null);
        try {
            await api.post(`/rma/${id}${endpoint}`, { body });
            await fetchRMA();
            if (successMessage) {
                // Could use toast notification here
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setActionLoading(false);
        }
    };

    // Item-level actions
    const handleApproveItem = async (itemId) => {
        await performAction(`/items/${itemId}/approve`);
    };

    const handleRejectItem = async () => {
        if (!rejectReason.trim()) {
            toast.warning("Please provide a rejection reason");
            return;
        }
        await performAction(`/items/${showRejectItemModal}/reject`, { reason: rejectReason });
        setShowRejectItemModal(null);
        setRejectReason("");
    };

    const handleApproveAll = async () => {
        await performAction("/approve-all");
    };

    // Repair actions - simplified QC workflow
    const handleItemQCReady = (itemId) => performAction(`/items/${itemId}/repair-status`, { status: "quality_check" });
    const handleItemPassQC = (itemId) => performAction(`/items/${itemId}/repair-status`, { status: "completed" });
    const handleItemFailQC = (itemId) => performAction(`/items/${itemId}/repair-status`, { status: "in_repair" });

    // RMA-level actions
    const handleGenerateLabel = () => performAction("/generate-label");
    const handleReceive = () => performAction("/receive");
    const handleGenerateReturnLabel = () => performAction("/generate-return-label");
    const handleConfirmShipped = () => performAction("/confirm-shipped");
    const handleComplete = () => performAction("/complete");

    const handleHold = () => {
        if (!holdReason.trim()) {
            toast.warning("Please provide a hold reason");
            return;
        }
        performAction("/hold", { reason: holdReason });
        setShowHoldModal(false);
        setHoldReason("");
    };

    const handleResume = () => performAction("/resume");

    const handleUpdateEstimatedDate = async () => {
        await performAction("/update", { estimated_completion_date: estimatedDate || null });
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        try {
            await api.post(`/rma/${id}/message`, {
                body: {
                    message: newMessage.trim(),
                    is_internal: false
                }
            });
            setNewMessage("");
            setShouldScrollMessages(true);
            await fetchRMA();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleSendInternalNote = async (e) => {
        e.preventDefault();
        if (!newInternalNote.trim()) return;

        try {
            await api.post(`/rma/${id}/message`, {
                body: {
                    message: newInternalNote.trim(),
                    is_internal: true
                }
            });
            setNewInternalNote("");
            await fetchRMA();
        } catch (err) {
            setError(err.message);
        }
    };

    const getStatusIndex = (status) => {
        if (status === "rejected" || status === "on_hold" || status === "cancelled" || status === "partially_approved") {
            return -1;
        }
        return statusSteps.findIndex(s => s.key === status);
    };

    if (loading) {
        return <LogoSpinner label="Loading RMA details" />;
    }

    if (!rma) {
        return (
            <EmptyState variant="area" title="RMA not found" action={<BackLink to="/rma" label="RMA List" />} />
        );
    }

    const currentStatus = rma.derived_status || rma.status;
    const currentStatusIndex = getStatusIndex(currentStatus);
    const items = rma.items || [];
    const pendingItems = items.filter(i => i.item_status === "pending_review");
    const approvedItems = items.filter(i => i.item_status === "approved");
    const rejectedItems = items.filter(i => i.item_status === "rejected");
    const activeItems = items.filter(i => i.item_status !== "rejected");

    // Group shipments by direction
    const shipments = rma.shipments || [];
    const inboundShipments = shipments.filter(s => s.direction === "inbound");
    const outboundShipments = shipments.filter(s => s.direction === "outbound");

    const Layout = rmaNumberOverride ? EmbeddedLayout : PageLayout;

    return (
        <div style={{ fontFamily: "RobotoMono, sans-serif" }}>
        <Layout
            kind="record"
            className="pt-4"
            rail={(
                <>
                    {/* Customer Info */}
                    <Panel level={3} title="Customer Information">
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-on-surface-variant">Name</span>
                                <span className="text-white">{rma.customer_name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-on-surface-variant">Email</span>
                                <a href={`mailto:${rma.customer_email}`} className="text-primary hover:underline">
                                    {rma.customer_email}
                                </a>
                            </div>
                            {rma.organization && (
                                <div className="flex justify-between">
                                    <span className="text-on-surface-variant">Organization</span>
                                    <span className="text-white">{rma.organization}</span>
                                </div>
                            )}
                            {rma.customer_phone && (
                                <div className="flex justify-between">
                                    <span className="text-on-surface-variant">Phone</span>
                                    <span className="text-white">{rma.customer_phone}</span>
                                </div>
                            )}
                        </div>
                    </Panel>

                    {/* Shipping Info */}
                    {(rma.address_line1 || shipments.length > 0) && (
                        <Panel level={3} title="Shipping Information">

                            {rma.address_line1 && (
                                <div className="mb-4">
                                    <span className="text-on-surface-variant text-sm">Customer Address</span>
                                    <p className="text-white">
                                        {rma.address_line1}
                                        {rma.address_line2 && <><br />{rma.address_line2}</>}
                                        <br />
                                        {rma.city}, {rma.state} {rma.zip}
                                        <br />
                                        {rma.country}
                                    </p>
                                </div>
                            )}

                            {/* Inbound Shipments */}
                            {inboundShipments.length > 0 && (
                                <div className="mb-4">
                                    <span className="text-on-surface-variant text-sm">Inbound Shipments ({inboundShipments.length})</span>
                                    <div className="space-y-2 mt-2">
                                        {inboundShipments.map((shipment, idx) => (
                                            <div key={shipment.id} className="p-2 bg-surface-container rounded">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-on-surface-variant">Box {idx + 1}</span>
                                                    {shipment.tracking_number && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-mono text-sm text-white">{shipment.tracking_number}</span>
                                                            <a
                                                                href={`https://www.fedex.com/fedextrack/?tracknumbers=${shipment.tracking_number}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary hover:underline text-sm"
                                                            >
                                                                Track
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                                {shipment.box_name && (
                                                    <span className="text-xs text-on-surface-variant">{shipment.box_name}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Outbound Shipments */}
                            {outboundShipments.length > 0 && (
                                <div>
                                    <span className="text-on-surface-variant text-sm">Return Shipments ({outboundShipments.length})</span>
                                    <div className="space-y-2 mt-2">
                                        {outboundShipments.map((shipment, idx) => (
                                            <div key={shipment.id} className="p-2 bg-surface-container rounded">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-on-surface-variant">Box {idx + 1}</span>
                                                    {shipment.tracking_number && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-mono text-sm text-white">{shipment.tracking_number}</span>
                                                            <a
                                                                href={`https://www.fedex.com/fedextrack/?tracknumbers=${shipment.tracking_number}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary hover:underline text-sm"
                                                            >
                                                                Track
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Panel>
                    )}

                    {/* Status History */}
                    <Panel level={3} title="Status History">
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                            {statusHistory.length === 0 ? (
                                <EmptyState variant="area" dense>No history yet</EmptyState>
                            ) : (
                                statusHistory.map((item, idx) => (
                                    <div key={idx} className="flex items-start gap-3">
                                        <div className={`w-2 h-2 mt-2 rounded-full ${
                                            item.change_type === "item_approval"
                                                ? item.new_value === "approved" ? "bg-green-500" : "bg-red-500"
                                                : item.change_type === "item_repair"
                                                ? "bg-blue-500"
                                                : "bg-outline"
                                        }`} />
                                        <div className="flex-1">
                                            <div className="flex justify-between">
                                                <span className="text-white text-sm">
                                                    {item.change_type === "item_approval" ? (
                                                        <>
                                                            Item {formatStatus(item.new_value)}
                                                            {item.item_serial && (
                                                                <span className="text-on-surface-variant ml-1">({item.item_serial})</span>
                                                            )}
                                                        </>
                                                    ) : item.change_type === "item_repair" ? (
                                                        <>
                                                            Repair: {formatStatus(item.new_value)}
                                                            {item.item_serial && (
                                                                <span className="text-on-surface-variant ml-1">({item.item_serial})</span>
                                                            )}
                                                        </>
                                                    ) : item.old_value ? (
                                                        <>{formatStatus(item.old_value)} → {formatStatus(item.new_value)}</>
                                                    ) : (
                                                        formatStatus(item.new_value || item.new_status)
                                                    )}
                                                </span>
                                                <span className="text-on-surface-variant text-xs">
                                                    {formatDate(item.created_at)}
                                                </span>
                                            </div>
                                            {item.changed_by && (
                                                <div className="text-on-surface-variant text-xs">by {item.changed_by}</div>
                                            )}
                                            {item.notes && (
                                                <p className="text-on-surface-variant text-sm mt-1">{item.notes}</p>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </Panel>
                </>
            )}
            aside={(
                <div className="space-y-6">
                    {/* Messages */}
                    <Panel level={3} title="Messages">

                        <div ref={messagesContainerRef} className="space-y-3 max-h-80 overflow-y-auto mb-4">
                            {messages.filter(m => !m.is_internal).length === 0 ? (
                                <EmptyState variant="area" dense>No messages yet</EmptyState>
                            ) : (
                                messages.filter(m => !m.is_internal).map((msg, idx) => (
                                    <div
                                        key={idx}
                                        className={`p-3 rounded ${
                                            msg.sender_type === "customer"
                                                ? "bg-blue-900/30 border-l-4 border-blue-500"
                                                : "bg-surface-container border-l-4 border-outline-variant"
                                        }`}
                                    >
                                        <div className="flex justify-between text-xs text-on-surface-variant mb-1">
                                            <span>{msg.sender_name || msg.sender_type}</span>
                                            <span>{formatDate(msg.created_at)}</span>
                                        </div>
                                        <p className="text-white whitespace-pre-wrap text-sm">{msg.message_text}</p>
                                    </div>
                                ))
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        <form onSubmit={handleSendMessage} className="flex gap-2">
                            <TextArea
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                rows={2}
                                aria-label="Reply to customer"
                                className="flex-1"
                                placeholder="Reply to customer..."
                            />
                            <button
                                type="submit"
                                disabled={!newMessage.trim()}
                                className={`${buttonClass('info')} self-end`}
                            >
                                Send
                            </button>
                        </form>
                    </Panel>

                    {/* Internal Notes */}
                    <Panel
                        level={3}
                        title="Internal Notes"
                        actions={<span className="text-xs text-amber-300">Staff only</span>}
                    >
                        <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                            {messages.filter(m => m.is_internal).length === 0 ? (
                                <EmptyState variant="area" dense>No internal notes yet</EmptyState>
                            ) : (
                                messages.filter(m => m.is_internal).map((msg, idx) => (
                                    <div
                                        key={idx}
                                        className="p-3 rounded bg-yellow-900/20 border-l-4 border-yellow-500"
                                    >
                                        <div className="flex justify-between text-xs text-on-surface-variant mb-1">
                                            <span>{msg.sender_name || msg.sender_type}</span>
                                            <span>{formatDate(msg.created_at)}</span>
                                        </div>
                                        <p className="text-white whitespace-pre-wrap text-sm">{msg.message_text}</p>
                                    </div>
                                ))
                            )}
                        </div>

                        <form onSubmit={handleSendInternalNote} className="flex gap-2">
                            <TextArea
                                value={newInternalNote}
                                onChange={(e) => setNewInternalNote(e.target.value)}
                                rows={2}
                                aria-label="Add internal note"
                                className="flex-1"
                                placeholder="Add internal note..."
                            />
                            <button
                                type="submit"
                                disabled={!newInternalNote.trim()}
                                className={`${buttonClass('filled')} self-end`}
                            >
                                Add
                            </button>
                        </form>
                    </Panel>
                </div>
            )}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <BackLink to="/rma" label="RMA List" className="mb-2" />
                    <h1 className="text-2xl font-bold text-white">{rma.rma_number}</h1>
                    {rma.estimated_completion_date && (
                        <p className="text-on-surface-variant text-sm">
                            Est. Completion: {formatDateShort(rma.estimated_completion_date)}
                        </p>
                    )}
                </div>
                <Tag hex={statusHex[currentStatus]}>
                    {formatStatus(currentStatus)}
                </Tag>
            </div>

            <Reveal show={!!error}><Alert className="mb-4">{error}</Alert></Reveal>

            {/* Status Progress */}
            {currentStatusIndex >= 0 && (
                <StepStrip
                    label="RMA progress"
                    className="mb-6"
                    steps={statusSteps.map((step, idx) => ({
                        key: step.key,
                        label: step.label,
                        state: idx < currentStatusIndex ? "done" : idx === currentStatusIndex ? "current" : "future",
                    }))}
                />
            )}

            {/* Actions & Estimated Date Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* RMA Actions */}
                <Panel level={3} title="RMA Actions">
                    <div className="flex flex-wrap gap-2">
                        {currentStatus === "approved" && approvedItems.length > 0 && (
                            <button
                                onClick={handleGenerateLabel}
                                disabled={actionLoading}
                                className={buttonClass('info')}
                            >
                                Generate Labels
                            </button>
                        )}

                        {currentStatus === "awaiting_receipt" && (
                            <button
                                onClick={handleReceive}
                                disabled={actionLoading}
                                className={buttonClass('info')}
                            >
                                Mark Received
                            </button>
                        )}

                        {currentStatus === "ready_to_ship" && (
                            <>
                                <button
                                    onClick={handleGenerateReturnLabel}
                                    disabled={actionLoading}
                                    className={buttonClass('tonal')}
                                >
                                    Generate Return Label
                                </button>
                                <button
                                    onClick={handleConfirmShipped}
                                    disabled={actionLoading}
                                    className={buttonClass('filled')}
                                >
                                    Confirm Shipped
                                </button>
                            </>
                        )}

                        {currentStatus === "shipped" && (
                            <button
                                onClick={handleComplete}
                                disabled={actionLoading}
                                className={buttonClass('good')}
                            >
                                Mark Complete
                            </button>
                        )}

                        {!["completed", "cancelled", "rejected"].includes(currentStatus) && (
                            <button
                                onClick={() => setShowHoldModal(true)}
                                disabled={actionLoading}
                                className={buttonClass('warn')}
                            >
                                Put On Hold
                            </button>
                        )}

                        {currentStatus === "on_hold" && (
                            <button
                                onClick={handleResume}
                                disabled={actionLoading}
                                className={buttonClass('good')}
                            >
                                Resume
                            </button>
                        )}
                    </div>
                    <Reveal show={actionLoading}>
                        <div className="mt-3 flex items-center gap-2 text-on-surface-variant text-sm">
                            <LogoSpinner size="inline" />
                            Processing...
                        </div>
                    </Reveal>
                </Panel>

                {/* Estimated Completion Date */}
                <Panel level={3} title="Estimated Completion">
                    <div className="flex gap-2">
                        <Field
                            type="date"
                            aria-label="Estimated completion date"
                            value={estimatedDate}
                            onChange={(e) => setEstimatedDate(e.target.value)}
                            className="flex-1"
                        />
                        <button
                            onClick={handleUpdateEstimatedDate}
                            disabled={actionLoading}
                            className={buttonClass('quiet')}
                        >
                            Update
                        </button>
                    </div>
                </Panel>
            </div>

            {/* Items Section */}
            <Panel
                level={3}
                className="mb-6"
                title={(
                    <>
                        Items ({activeItems.length})
                        {rejectedItems.length > 0 && (
                            <span className="text-on-surface-variant font-normal ml-2">
                                + {rejectedItems.length} rejected
                            </span>
                        )}
                    </>
                )}
                actions={pendingItems.length > 0 ? (
                    <button
                        onClick={handleApproveAll}
                        disabled={actionLoading}
                        className={buttonClass('good')}
                    >
                        Approve All ({pendingItems.length})
                    </button>
                ) : undefined}
            >
                {/* Active Items (pending + approved) */}
                <div className="space-y-3">
                    {activeItems.map((item) => {
                        const isHighlighted = highlightSerial && item.serial_number?.toUpperCase() === highlightSerial.toUpperCase();
                        return (
                        <div
                            key={item.id}
                            className={`p-4 rounded border transition-all duration-300 ${
                                isHighlighted
                                    ? "bg-primary/20 border-primary border-2 shadow-[0_0_12px_rgba(255,204,51,0.4)]"
                                    : item.item_status === "rejected"
                                    ? "bg-red-900/20 border-red-800"
                                    : item.item_status === "approved"
                                    ? "bg-green-900/10 border-green-800/50"
                                    : "bg-surface-container border-outline-variant"
                            }`}
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-medium text-white">
                                            {item.catalog_name || item.custom_description || "Item"}
                                        </span>
                                        {item.quantity > 1 && (
                                            <span className="text-on-surface-variant">×{item.quantity}</span>
                                        )}
                                        <Tag hex={itemStatusHex[item.item_status]}>
                                            {formatStatus(item.item_status)}
                                        </Tag>
                                        {item.item_status === "approved" && item.repair_status && (
                                            <Tag hex={repairStatusHex[item.repair_status]}>
                                                Repair: {formatStatus(item.repair_status)}
                                            </Tag>
                                        )}
                                    </div>
                                    {/* Serial Number or Charger ID */}
                                    {item.serial_number && (
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-primary font-bold text-lg font-mono">
                                                {item.serial_number}
                                            </span>
                                            {item.catalog_item_type === "drone" && (
                                                <Link
                                                    to={`/drone/${item.serial_number}`}
                                                    className="text-on-surface-variant hover:text-white text-xs"
                                                >
                                                    View Drone →
                                                </Link>
                                            )}
                                            {item.has_log && (
                                                <span className="px-2 py-0.5 bg-green-900/50 text-green-300 text-xs rounded flex items-center gap-1">
                                                    📄 Log Uploaded
                                                    {item.log_url && (
                                                        <a href={item.log_url} target="_blank" rel="noopener noreferrer" className="underline ml-1">
                                                            View
                                                        </a>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {item.charger_id && (
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-on-surface-variant text-sm">ID:</span>
                                            <span className="text-primary font-bold text-lg font-mono">
                                                {item.charger_id}
                                            </span>
                                        </div>
                                    )}
                                    {/* Issue Category */}
                                    {item.issue_category && (
                                        <span className="inline-block mt-1 px-2 py-0.5 bg-blue-900/50 text-blue-300 text-xs rounded">
                                            {item.issue_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </span>
                                    )}
                                    {/* Issue Description */}
                                    {item.issue_description && (
                                        <p className="text-sm text-on-surface mt-2">
                                            {item.issue_description}
                                        </p>
                                    )}
                                    {item.rejection_reason && (
                                        <p className="text-sm text-red-400 mt-1">
                                            Rejection: {item.rejection_reason}
                                        </p>
                                    )}
                                    {item.repair_notes && (
                                        <p className="text-sm text-blue-400 mt-1">
                                            Repair: {item.repair_notes}
                                        </p>
                                    )}
                                </div>

                                {/* Item Actions */}
                                <div className="flex flex-col gap-2 ml-4">
                                    {item.item_status === "pending_review" && (
                                        <>
                                            <button
                                                onClick={() => handleApproveItem(item.id)}
                                                disabled={actionLoading}
                                                className={buttonClass('good', 'sm')}
                                            >
                                                Approve
                                            </button>
                                            <button
                                                onClick={() => setShowRejectItemModal(item.id)}
                                                disabled={actionLoading}
                                                className={buttonClass('danger-solid', 'sm')}
                                            >
                                                Reject
                                            </button>
                                        </>
                                    )}
                                    {/* Ready for QC button - shows when item is received or in_repair */}
                                    {item.item_status === "approved" && ["received", "in_repair"].includes(item.repair_status) && (
                                        <button
                                            onClick={() => handleItemQCReady(item.id)}
                                            disabled={actionLoading}
                                            className={buttonClass('info', 'sm')}
                                        >
                                            Ready for QC
                                        </button>
                                    )}
                                    {/* Pass/Fail QC buttons - shows when item is in quality_check */}
                                    {item.item_status === "approved" && item.repair_status === "quality_check" && (
                                        <>
                                            <button
                                                onClick={() => handleItemPassQC(item.id)}
                                                disabled={actionLoading}
                                                className={buttonClass('good', 'sm')}
                                            >
                                                Pass QC
                                            </button>
                                            <button
                                                onClick={() => handleItemFailQC(item.id)}
                                                disabled={actionLoading}
                                                className={buttonClass('danger', 'sm')}
                                            >
                                                Fail QC
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )})}
                </div>

                {/* Rejected Items (collapsed by default) */}
                {rejectedItems.length > 0 && (
                    <div className="mt-4 border-t border-gray-700 pt-4">
                        <button
                            onClick={() => setShowRejectedItems(!showRejectedItems)}
                            className="flex items-center gap-2 text-on-surface-variant hover:text-white transition text-sm"
                        >
                            <span className={`transform transition-transform ${showRejectedItems ? 'rotate-90' : ''}`}>
                                ▶
                            </span>
                            Rejected Items ({rejectedItems.length})
                        </button>
                        <Reveal show={showRejectedItems}>
                            <div className="space-y-3 mt-3">
                                {rejectedItems.map((item) => (
                                    <div
                                        key={item.id}
                                        className="p-4 rounded border bg-red-900/20 border-red-800/50 opacity-75"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-medium text-white">
                                                        {item.catalog_name || item.custom_description || "Item"}
                                                    </span>
                                                    {item.quantity > 1 && (
                                                        <span className="text-on-surface-variant">×{item.quantity}</span>
                                                    )}
                                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-800 text-red-200">
                                                        Rejected
                                                    </span>
                                                </div>
                                                {item.serial_number && (
                                                    <span className="text-on-surface-variant font-mono text-sm">
                                                        {item.serial_number}
                                                    </span>
                                                )}
                                                {item.charger_id && (
                                                    <span className="text-on-surface-variant font-mono text-sm ml-2">
                                                        ID: {item.charger_id}
                                                    </span>
                                                )}
                                                {item.rejection_reason && (
                                                    <p className="text-sm text-red-400 mt-2">
                                                        Reason: {item.rejection_reason}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                )}
            </Panel>

            <Dialog
                open={!!showRejectItemModal}
                onClose={() => { setShowRejectItemModal(null); setRejectReason(""); }}
                dirty={rejectReason !== ""}
                title="Reject item"
                tone="danger"
                dismissible={false}
                size="md"
                footer={(
                    <>
                        <button
                            onClick={() => { setShowRejectItemModal(null); setRejectReason(""); }}
                            className={buttonClass('quiet')}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleRejectItem}
                            className={buttonClass('danger-solid')}
                        >
                            Reject
                        </button>
                    </>
                )}
            >
                <TextArea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={4}
                    aria-label="Reason for rejection"
                    className="w-full"
                    placeholder="Reason for rejection..."
                />
            </Dialog>

            <Dialog
                open={showHoldModal}
                dismissible={false}
                onClose={() => { setShowHoldModal(false); setHoldReason(""); }}
                dirty={holdReason !== ""}
                title="Put on hold"
                size="md"
                footer={(
                    <>
                        <button
                            onClick={() => { setShowHoldModal(false); setHoldReason(""); }}
                            className={buttonClass('quiet')}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleHold}
                            className={buttonClass('warn')}
                        >
                            Put on hold
                        </button>
                    </>
                )}
            >
                <TextArea
                    value={holdReason}
                    onChange={(e) => setHoldReason(e.target.value)}
                    rows={4}
                    aria-label="Reason for hold"
                    className="w-full"
                    placeholder="Reason for hold..."
                />
            </Dialog>
        </Layout>
        </div>
    );
};

export default RMADetails;
